import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, ShieldCheck } from "lucide-react";
import { api, fileUrl, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Chat() {
    const { matchId } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [match, setMatch] = useState(null);
    const [other, setOther] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const wsRef = useRef(null);

    const scrollToBottom = () => {
        requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
        });
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [m, msgs] = await Promise.all([
                    api.get(`/matches/${matchId}`),
                    api.get(`/messages/${matchId}`),
                ]);
                if (cancelled) return;
                setMatch(m.data.match);
                setOther(m.data.other_user);
                setMessages(msgs.data);
                scrollToBottom();
            } catch {
                nav("/matches");
            }
        })();
        return () => { cancelled = true; };
    }, [matchId, nav]);

    // WebSocket for live updates. Token via query param (cookies + ws across ingress can be spotty)
    useEffect(() => {
        if (!matchId) return;
        let token = null;
        try {
            // Pull token from cookie if present (non-http-only would require server, skip)
            // Instead, the server accepts ?token= fallback. We'll use auth/token handshake via a helper endpoint.
        } catch {}
        const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/ws/chat/${matchId}`;
        let ws;
        try {
            ws = new WebSocket(wsUrl);
            wsRef.current = ws;
            ws.onmessage = (ev) => {
                try {
                    const data = JSON.parse(ev.data);
                    if (data.type === "message") {
                        setMessages((m) => {
                            if (m.find((x) => x.message_id === data.message.message_id)) return m;
                            return [...m, data.message];
                        });
                        scrollToBottom();
                    }
                } catch {}
            };
            ws.onerror = () => { /* silent */ };
        } catch {}
        return () => {
            try { ws && ws.close(); } catch {}
        };
    }, [matchId]);

    // Polling fallback every 5s
    useEffect(() => {
        if (!matchId) return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/messages/${matchId}`);
                setMessages((prev) => (data.length !== prev.length ? data : prev));
            } catch {}
        }, 5000);
        return () => clearInterval(t);
    }, [matchId]);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || sending) return;
        setSending(true);
        const content = text;
        setText("");
        try {
            const { data } = await api.post("/messages", { match_id: matchId, content });
            setMessages((m) => {
                if (m.find((x) => x.message_id === data.message_id)) return m;
                return [...m, data];
            });
            scrollToBottom();
        } finally {
            setSending(false);
        }
    };

    if (!match || !other || !user) {
        return <div className="min-h-screen flex items-center justify-center text-white/50 text-xs uppercase tracking-widest">Loading…</div>;
    }

    return (
        <div className="min-h-screen flex flex-col">
            <header className="sticky top-0 z-40 bg-ink-950/95 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center gap-3">
                <button
                    onClick={() => nav("/matches")}
                    data-testid="chat-back-btn"
                    className="text-white/70 hover:text-white"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-9 h-9 bg-ink-800 overflow-hidden border border-white/10">
                    {other.photos?.[0] ? (
                        <img src={fileUrl(other.photos[0])} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center font-display text-sm text-white/40">
                            {other.name?.[0] || "?"}
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                        <h2 className="font-display uppercase text-white truncate" data-testid="chat-other-name">{other.name}</h2>
                        {other.verified && <ShieldCheck className="w-3.5 h-3.5 text-dash" />}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Matched</div>
                </div>
            </header>

            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
                data-testid="chat-message-list"
            >
                {messages.length === 0 && (
                    <div className="text-center text-xs text-white/40 uppercase tracking-widest pt-20">
                        Break the ice.
                    </div>
                )}
                {messages.map((m) => {
                    const mine = m.sender_id === user.user_id;
                    return (
                        <div
                            key={m.message_id}
                            className={`flex ${mine ? "justify-end" : "justify-start"}`}
                            data-testid={`chat-msg-${m.message_id}`}
                        >
                            <div
                                className={`max-w-[75%] px-3 py-2 text-sm break-words ${
                                    mine
                                        ? "bg-smash text-white rounded-tl-xl rounded-tr-xl rounded-bl-xl"
                                        : "bg-ink-700 text-white rounded-tl-xl rounded-tr-xl rounded-br-xl"
                                }`}
                            >
                                {m.content}
                            </div>
                        </div>
                    );
                })}
            </div>

            <form
                onSubmit={send}
                className="sticky bottom-0 bg-ink-950/95 backdrop-blur-xl border-t border-white/10 p-3 flex gap-2"
                data-testid="chat-form"
            >
                <input
                    type="text"
                    placeholder="Type something…"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    data-testid="chat-input"
                    className="flex-1 h-11 bg-ink-800 border border-white/10 focus:border-smash px-3 text-white outline-none"
                />
                <button
                    type="submit"
                    disabled={!text.trim() || sending}
                    data-testid="chat-send-btn"
                    className="h-11 w-11 bg-smash hover:bg-smash/90 disabled:opacity-40 text-white flex items-center justify-center"
                >
                    <Send className="w-4 h-4" />
                </button>
            </form>
        </div>
    );
}
